import { useEffect, useRef, useState } from 'react';
import { openDocumentCameraStream, captureFrame } from '../../platform/camera';
import { reportError } from '../../core/telemetry/report';
import { t } from '../../platform/lang';

/**
 * Proof-of-delivery photo. Live in-app camera ONLY (31 Oct, owner: letting
 * them upload photos "is unacceptable"). The old backup -- the phone's own
 * camera app through a file picker -- could hand over a picture from the
 * phone's library on some phones. If the in-app camera will not open, the
 * driver tries again or calls the office; the office has "mark delivered".
 * Photo is MANDATORY for completion (server-enforced).
 */
export function PhotoCapture(props: {
  onCapture: (blob: Blob) => void;
  onCancel: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [streamFailed, setStreamFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [preview, setPreview] = useState<{ blob: Blob; url: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    openDocumentCameraStream()
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch((e: unknown) => {
        reportError('camera_failed', 'photo', String((e as Error)?.message ?? e).slice(0, 120));
        setStreamFailed(true);
      });
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [attempt]);

  /*
   * A preview that was never accepted or retaken still holds its object URL,
   * and with it the photo. Accept and Retake both revoke it, but leaving the
   * screen another way -- going back a step, the stop being abandoned -- did
   * not, so the image stayed in memory until the page went away. Small on its
   * own; it is a full-size photo on a phone that is about to re-render the
   * route.
   */
  useEffect(() => () => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
  }, []);

  /* Mirrors `preview.url` so the unmount cleanup above can reach it. */
  const previewRef = useRef<string | null>(null);
  useEffect(() => { previewRef.current = preview?.url ?? null; }, [preview]);

  const shoot = async () => {
    const video = videoRef.current;
    if (!video) return;
    const hadFrame = video.videoWidth > 0;
    const blob = await captureFrame(video);
    // The screen can close while the picture is being made (30 Oct: 12 crashes
    // on one phone reading the camera after it was gone). Nothing to show then.
    if (!videoRef.current) return;
    // A camera that opens but hands back nothing -- the usual shape of a
    // browser-update bug -- is shown as not working, never as a blank photo.
    if (!blob || blob.size < 2000 || !hadFrame) {
      reportError('camera_failed', 'photo', 'empty frame');
      streamRef.current?.getTracks().forEach((tr) => tr.stop());
      setStreamFailed(true);
      return;
    }
    setPreview({ blob, url: URL.createObjectURL(blob) });
  };

  const accept = () => {
    if (!preview) return;
    URL.revokeObjectURL(preview.url);
    props.onCapture(preview.blob);
  };

  if (preview) {
    return (
      <div className="photo-capture">
        <img src={preview.url} alt="Delivery proof preview" className="photo-preview" />
        <div className="row">
          <button className="btn secondary"
                  onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }}>
            Retake
          </button>
          <button className="btn primary" onClick={accept}>Use photo</button>
        </div>
      </div>
    );
  }

  return (
    <div className="photo-capture">
      <p className="hint">Photo of delivered items (required)</p>
      {!streamFailed ? (
        <>
          <video ref={videoRef} autoPlay playsInline muted className="viewfinder doc-view" />
          <div className="row">
            <button className="btn secondary" onClick={props.onCancel}>Cancel</button>
            <button className="btn primary" onClick={shoot}>Take photo</button>
          </div>
        </>
      ) : (
        <>
          <div className="banner warn-banner">{t('photoCameraStuck')}</div>
          <div className="row">
            <button className="btn secondary" onClick={props.onCancel}>Cancel</button>
            <button className="btn primary" onClick={() => { setStreamFailed(false); setAttempt((n) => n + 1); }}>
              {t('photoTryAgain')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
